// src/components/mkh/Answer.js
import React, { useState, useRef } from "react";
import { FaPause, FaPlay } from "react-icons/fa";
import {
  FaClockRotateLeft,
  FaMoneyBillTransfer,
  FaForward,
  FaBackward,
  FaXmark,
} from "react-icons/fa6";
import { HiPhone } from "react-icons/hi2";
import { TbSquareHalf } from "react-icons/tb";
import { useStopwatch } from "react-timer-hook";
import ReactPlayer from "react-player";
import textSizeBasedOnLength from "../../funcs/textResizer";
import { truncateText } from "../../utils/textUtils";
import TextModal from "../modals/TextModal";

const MKHAnswer = ({
  game,
  ads,
  question,
  setShowAnswer,
  setShowWhoAnswered,
}) => {
  const [fullScreen, setFullScreen] = useState(false);
  const [showTextModal, setShowTextModal] = useState(false);
  const { seconds, minutes, isRunning, start, pause, reset } = useStopwatch({
    autoStart: true,
  });

  // Audio player state
  const audioRef = useRef(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioPosition, setAudioPosition] = useState(0);
  // eslint-disable-next-line
  const [audioLoaded, setAudioLoaded] = useState(false);

  const modeName = game?.modes?.find((mode) =>
    mode.questions.find(
      (q) => (q?.question?._id || q?.customQuestion?._id) === question?._id
    )
  )?.name;

  const answerText = question?.answer || "";
  const { truncated: truncatedAnswer, needsTruncation } = truncateText(
    answerText,
    120
  );

  // Audio player functions
  const handleAudioPlayPause = () => {
    setIsAudioPlaying(!isAudioPlaying);
  };

  const handleAudioSkipForward = () => {
    if (audioRef.current) {
      const currentTime = audioRef.current.getCurrentTime();
      audioRef.current.seekTo(currentTime + 15);
    }
  };

  const handleAudioSkipBackward = () => {
    if (audioRef.current) {
      const currentTime = audioRef.current.getCurrentTime();
      audioRef.current.seekTo(Math.max(0, currentTime - 15));
    }
  };

  const handleAudioProgress = (state) => {
    setAudioPosition(state.playedSeconds);
  };

  const handleAudioDuration = (duration) => {
    setAudioDuration(duration);
    setAudioLoaded(true);
  };

  const handleAudioSeek = (e) => {
    const seekValue = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.seekTo(seekValue);
    }
    setAudioPosition(seekValue);
  };

  const formatTime = (timeInSeconds) => {
    if (isNaN(timeInSeconds)) return "0:00";
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  return (
    <div className="w-full h-[90vh] bg-[#eaf6fb] flex flex-col">
      {/* Header Card - Thinner */}
      <div className="p-2 flex-shrink-0">
        <div className="bg-white rounded-xl p-3 shadow-lg border-2 border-[#bfe4f3]">
          <div className="flex justify-between items-center">
            {/* Left - Mode Info */}
            <div className="flex items-center space-x-3">
              <div className="bg-[#29a6d6] rounded-lg px-3 py-1">
                <h2 className="text-white font-bold text-sm">{modeName}</h2>
              </div>
              <div className="bg-[#bfe4f3] rounded-lg px-3 py-1">
                <h2 className="text-[#29a6d6] font-bold text-sm">
                  {question?.points} نقطة
                </h2>
              </div>
              <div className="bg-[#29a6d6] rounded-lg px-3 py-1">
                <h2 className="text-white font-bold text-sm">الإجابة</h2>
              </div>
              {question?.custom && (
                <div className="bg-[#eaf6fb] border-2 border-[#29a6d6] rounded-lg px-2 py-1">
                  <span className="text-[#29a6d6] font-semibold text-xs">
                    فئة خاصة
                  </span>
                </div>
              )}
            </div>

            {/* Center - Timer */}
            <div className="bg-[#29a6d6] rounded-xl p-2 shadow-lg">
              <div className="flex items-center space-x-4 text-white">
                <button onClick={() => (isRunning ? pause() : start())}>
                  {isRunning ? <FaPause size={16} /> : <FaPlay size={16} />}
                </button>
                <div className="text-lg font-mono font-bold">
                  {minutes.toString().padStart(2, "0")}:
                  {seconds.toString().padStart(2, "0")}
                </div>
                <button onClick={reset}>
                  <FaClockRotateLeft size={16} />
                </button>
              </div>
            </div>

            {/* Right - Action Buttons */}
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowAnswer(false)}
                className="bg-[#bfe4f3] hover:bg-[#a8d4e8] text-[#29a6d6] px-4 py-2 rounded-lg font-bold transition-colors text-sm"
              >
                السؤال
              </button>
              <button
                onClick={() => setShowWhoAnswered(true)}
                className="bg-[#29a6d6] hover:bg-[#1e7ba6] text-white px-4 py-2 rounded-lg font-bold transition-colors text-sm"
              >
                من جاوب؟
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex p-2 gap-2 flex-1 min-h-0">
        {/* Answer Content Card */}
        <div className="flex-1 min-w-0">
          <div className="bg-white rounded-xl p-4 shadow-lg border-2 border-[#bfe4f3] h-full flex flex-col">
            {/* Answer Text - Prominent Display */}
            <div className="mb-4 flex-shrink-0">
              <div className="bg-[#29a6d6] rounded-lg p-6 border border-[#bfe4f3] shadow-lg">
                <h1
                  className="text-white font-bold text-center leading-relaxed"
                  style={{
                    fontSize: textSizeBasedOnLength(answerText),
                    direction: "rtl",
                  }}
                  onClick={() => needsTruncation && setShowTextModal(true)}
                >
                  {needsTruncation ? truncatedAnswer : answerText}
                </h1>
                {needsTruncation && (
                  <button
                    onClick={() => setShowTextModal(true)}
                    className="mt-3 px-3 py-1 bg-white text-[#29a6d6] rounded-lg text-xs font-semibold transition-colors hover:bg-[#f8fafc]"
                  >
                    اقرأ المزيد
                  </button>
                )}
              </div>
            </div>

            {/* Answer Media Content */}
            <div className="flex-1 min-h-0">
              {/* Answer Video */}
              {question?.answerVideo && question?.answerVideo.length > 10 && (
                <div className="rounded-lg overflow-hidden border-2 border-[#bfe4f3] h-full">
                  <ReactPlayer
                    url={question?.answerVideo}
                    controls
                    width="100%"
                    height="100%"
                  />
                </div>
              )}

              {/* Answer Audio */}
              {question?.answerAudio && question?.answerAudio.length > 10 && (
                <div className="bg-[#eaf6fb] rounded-lg p-4 border border-[#bfe4f3] h-full flex flex-col justify-center">
                  <h3 className="text-[#29a6d6] font-bold mb-3 text-center text-sm">
                    ملف صوتي للإجابة
                  </h3>
                  <ReactPlayer
                    ref={audioRef}
                    url={question?.answerAudio}
                    playing={isAudioPlaying}
                    onProgress={handleAudioProgress}
                    onDuration={handleAudioDuration}
                    width="0"
                    height="0"
                  />
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={handleAudioPlayPause}
                      className="p-2 bg-[#29a6d6] text-white rounded-full hover:bg-[#1e7ba6]"
                    >
                      {isAudioPlaying ? (
                        <FaPause size={12} />
                      ) : (
                        <FaPlay size={12} />
                      )}
                    </button>
                    <button
                      onClick={handleAudioSkipBackward}
                      className="p-1 bg-[#bfe4f3] text-[#29a6d6] rounded hover:bg-[#a8d4e8]"
                    >
                      <FaBackward size={10} />
                    </button>
                    <input
                      type="range"
                      min="0"
                      max={audioDuration}
                      value={audioPosition}
                      onChange={handleAudioSeek}
                      className="flex-1 h-1 bg-[#bfe4f3] rounded appearance-none"
                      style={{
                        background: `linear-gradient(to right, #29a6d6 0%, #29a6d6 ${
                          (audioPosition / audioDuration) * 100
                        }%, #bfe4f3 ${
                          (audioPosition / audioDuration) * 100
                        }%, #bfe4f3 100%)`,
                      }}
                    />
                    <button
                      onClick={handleAudioSkipForward}
                      className="p-1 bg-[#bfe4f3] text-[#29a6d6] rounded hover:bg-[#a8d4e8]"
                    >
                      <FaForward size={10} />
                    </button>
                    <span className="text-[#29a6d6] text-xs font-semibold">
                      {formatTime(Math.floor(audioPosition))} /{" "}
                      {formatTime(Math.floor(audioDuration))}
                    </span>
                  </div>
                </div>
              )}

              {/* Answer Image */}
              {question?.answerImage && (
                <div className="rounded-lg overflow-hidden border-2 border-[#bfe4f3] h-full">
                  <button
                    onClick={() => setFullScreen(true)}
                    className="w-full h-full"
                  >
                    <img
                      src={question?.answerImage}
                      alt="الإجابة"
                      className="w-full h-full object-contain"
                    />
                  </button>
                </div>
              )}

              {/* Additional Info */}
              {question?.additionalInfo && (
                <div className="bg-[#eaf6fb] rounded-lg p-3 border border-[#bfe4f3] h-full flex flex-col justify-center">
                  <h3 className="text-[#29a6d6] font-bold mb-2 text-sm">
                    معلومات إضافية:
                  </h3>
                  <p
                    className="text-[#29a6d6] leading-relaxed text-sm"
                    style={{ direction: "rtl" }}
                  >
                    {question.additionalInfo}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Teams Sidebar */}
        <div className="w-60 flex-shrink-0">
          <div className="bg-white rounded-xl p-3 shadow-lg border-2 border-[#bfe4f3] h-full flex flex-col">
            <h3 className="text-[#29a6d6] font-bold mb-3 text-center text-sm flex-shrink-0">
              الفرق
            </h3>
            <div className="flex-1 flex flex-col h-full">
              {/* Teams Section - 1/4 of main height */}
              <div className="flex flex-col gap-2 h-full">
                {game?.teams?.map((team, index) => (
                  <div
                    key={index}
                    className={`bg-[#eaf6fb] rounded-lg p-2 border-2 flex-shrink-0 h-[24%] flex justify-between items-center ${
                      team.currentTurn ? "border-[#29a6d6]" : "border-[#bfe4f3]"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1 w-1/2">
                      <div className="flex items-center space-x-2">
                        <div className="w-6 h-6 rounded-full overflow-hidden border border-[#bfe4f3]">
                          <img
                            src={team?.image?.image}
                            alt={team?.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div>
                          <h4 className="font-bold text-[#29a6d6] text-xs">
                            {team.name}
                          </h4>
                          <p className="text-[#29a6d6] text-xs">
                            {team.points} نقطة
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Power-ups (Disabled) */}
                    <div className="flex space-x-1 justify-center w-1/2">
                      {team?.powerUps?.map((powerUp, powerUpIndex) => (
                        <button
                          key={powerUpIndex}
                          disabled
                          className="p-2 rounded text-s bg-gray-300 text-gray-500 cursor-not-allowed"
                        >
                          {powerUp?.name === "one" ? (
                            <FaMoneyBillTransfer size={10} />
                          ) : powerUp?.name === "two" ? (
                            <HiPhone size={10} />
                          ) : (
                            <TbSquareHalf size={10} />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Ads Section - Takes remaining space */}
              {ads && Array.isArray(ads) && ads.length > 0 && (
                <div className="flex-1 min-h-0">
                  <h4 className="text-[#29a6d6] font-bold mb-2 text-center text-xs">
                    الإعلانات
                  </h4>
                  <div className="space-y-1 h-full overflow-hidden">
                    {ads.slice(0, 4).map((ad, index) => (
                      <div
                        key={index}
                        className="rounded-lg overflow-hidden border border-[#bfe4f3]"
                        style={{
                          height: `calc((100% - ${
                            (Math.min(ads.length, 4) - 1) * 0.25
                          }rem) / ${Math.min(ads.length, 4)})`,
                        }}
                      >
                        <img
                          src={ad.image}
                          alt={ad.name}
                          className="w-full h-3/4 object-cover"
                        />
                        <div className="p-1 bg-[#eaf6fb] h-1/4 flex items-center justify-center">
                          <p className="text-[#29a6d6] text-xs font-semibold text-center truncate">
                            {ad.name}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Full Screen Image Modal */}
      {fullScreen && (
        <div
          onClick={() => setFullScreen(false)}
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50"
        >
          <button
            onClick={() => setFullScreen(false)}
            className="absolute top-4 right-4 text-white hover:text-gray-300"
          >
            <FaXmark size={30} />
          </button>
          <img
            src={question?.answerImage}
            alt="الإجابة"
            className="max-w-full h-full object-contain"
          />
        </div>
      )}

      {/* Text Modal */}
      <TextModal
        isOpen={showTextModal}
        onClose={() => setShowTextModal(false)}
        text={answerText}
        title="نص الإجابة"
      />
    </div>
  );
};

export default MKHAnswer;
